import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Home, 
  FolderOpen, 
  CreditCard, 
  Settings, 
  LogOut,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Menu,
  X,
  Inbox,
  Grid2X2PlusIcon,
  Users2Icon,
  User
} from 'lucide-react';
import {AiFillHome,AiFillDollarCircle} from 'react-icons/ai'
import {BsGridFill,BsCurrencyDollar, BsMessenger, BsInbox, BsGrid1X2} from 'react-icons/bs'
import {FaQuestionCircle} from 'react-icons/fa'
import { BiCategoryAlt, BiEnvelope, BiMessage } from 'react-icons/bi';
import { GrOrderedList } from 'react-icons/gr';
import { useAuthStore } from '../store/authStore';
import { getUnreadMessageCount } from '../services/message.service';
import { AxiosInstance } from '../services/auth/AxiosInstance';
import { useNavigate } from 'react-router-dom';



const MobileHeader = ({ onMenuToggle }) => (
  <div className="fixed top-0 left-0 right-0 h-14 bg-white dark:bg-[#10131a] z-50 flex items-center justify-between px-4 md:hidden border-b border-gray-100 dark:border-gray-800">
    <button
      onClick={onMenuToggle}
      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
    >
      <Menu size={20} className="text-gray-600" />
    </button>
    
    <a href="/" className="text-xl font-semibold text-[#174568]">
            Hello<span className="text-[#7295B8]">cer</span>
          </a>
    
    <div className="w-10"></div> {/* Spacer for centering logo */}
  </div>
);

// Mobile Sidebar Overlay Component
const MobileSidebarOverlay = ({ isOpen, onClose, currentPath, onNavigate, menuItems, logoutUser }) => {
  if (!isOpen) return null;

  const handleNavigation = (path) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      // Default navigation logic - you can customize this
      window.location.href = `/admins${path}`;
    }
    onClose(); // Close menu after navigation
  };

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      {/* Background overlay */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full bg-white dark:bg-[#10131a] transition-transform duration-300 w-80 max-w-[85vw] ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Header with close button */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
           <a href="/" className="text-xl font-semibold text-[#174568]">
            Hello<span className="text-[#7295B8]">cer</span>
          </a>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="mt-8">
          <ul className="w-[85%] space-y-2 px-4">
            {menuItems.map((item, index) => {
              const IconComponent = item.icon;
              const isActive = currentPath === item.path;
              
              return (
                <li key={index}>
                  <button
                    onClick={() => handleNavigation(item.path)}
                    className={`w-full flex items-center px-3 py-2 text-left transition-all duration-200 group ${
                      isActive 
                        ? 'text-[#174568] dark:text-blue-400 border-r-2 border-[#174568] dark:border-blue-400' 
                        : 'text-[#9D9D9D] dark:text-gray-400 hover:text-gray-500 dark:hover:text-gray-200'
                    }`}
                  >
                    <IconComponent 
                      size={20} 
                      className={`mr-3 ${
                        isActive ? 'text-[#174568] dark:text-blue-400' : 'text-[#9D9D9D] dark:text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-200'
                      }`} 
                    />
                    <span className="font-medium">{item.label}</span>
                  </button>
                </li>
              );
            })}

               <li onClick={logoutUser}>
                                   <button
                                        
                                          className={`flex items-center px-5 py-3 text-sm w-full text-left text-red-500 hover:bg-gray-100 dark:hover:bg-gray-800`}>
                                        <LogOut size={20} className="mr-3" /> 
                                        Logout
                                        </button>
                                      </li>

          </ul>
        </nav>

     

      </div>
    </div>
  );
};

// Desktop Sidebar Component
const DesktopSidebar = ({ currentPath, onNavigate, isCollapsed, onToggle, menuItems, logoutUser }) => {
  const handleNavigation = (path) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      // Default navigation logic - you can customize this
      window.location.href = `/admins${path}`;
    }
  };

  return (
    <div className={`hidden md:block fixed left-0 top-0 h-full bg-white dark:bg-[#10131a] shadow-md dark:shadow-gray-800/10 transition-all duration-300 z-40 ${
      isCollapsed ? 'w-16' : 'w-64'
    }`}>
      {/* Logo Section */}
      <div className="p-6 border-b border-gray-100 dark:border-gray-800">
        {!isCollapsed && (
          <a href="/" className="text-2xl font-semibold text-[#174568]">
            Hello<span className="text-[#7295B8]">cer</span>
          </a>
        )}
        {isCollapsed && (
          <div className="text-xl font-bold text-center">
            <span className="text-[#174568]">H</span>
          </div>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="mt-8">
        <ul className="space-y-2 px-4">
          {menuItems.map((item, index) => {
            const IconComponent = item.icon;
            const isActive = currentPath === item.path;
            
            return (
              <li key={index}>
                <button
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full flex items-center px-3 py-2 text-left transition-all duration-200 group ${
                    isActive 
                        ? 'text-[#174568] dark:text-blue-400 border-r-2 border-[#174568] dark:border-blue-400' 
                        : 'text-[#9D9D9D] dark:text-gray-400 hover:text-gray-500 dark:hover:text-gray-200'
                    }`}
                >
                  <IconComponent 
                    size={20} 
                    className={`${isCollapsed ? 'mx-auto' : 'mr-3'} ${
                                              isActive ? 'text-[#174568] dark:text-blue-400' : 'text-[#9D9D9D] dark:text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-200'
                    }`} 
                  />
                  {!isCollapsed && (
                    <span className="font-medium">{item.label}</span>
                  )}
                </button>
              </li>
            );
          })}


             <li onClick={logoutUser}>
                                 <button
                                      
                                        className={`flex items-center px-5 py-3 text-sm w-full text-left text-red-500 hover:bg-gray-100 dark:hover:bg-gray-800`}>
                                      <LogOut size={20} className="mr-3" /> 
                                      Logout
                                      </button>
                                    </li>


        </ul>
      </nav>

    

      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 w-6 h-6 bg-white dark:bg-[#1d2026] border border-gray-200 dark:border-[#32353c] rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow"
      >
        {isCollapsed ? (
          <ChevronRight size={12} className="text-gray-600 dark:text-gray-300" />
        ) : (
          <ChevronLeft size={12} className="text-gray-600 dark:text-gray-300" />
        )}
      </button>
    </div>
  );
};

const AdminSidebar = ({ currentPath, onNavigate, isCollapsed, onToggle }) => {
  const {clearTokens,role} = useAuthStore();
    const navigate = useNavigate();
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const collapsed = isCollapsed !== undefined ? isCollapsed : internalCollapsed;
  const toggleSidebar = onToggle || (() => setInternalCollapsed(!internalCollapsed));

  // Auto-detect current path if not provided
  const activePath = currentPath || (typeof window !== 'undefined' ? window.location.pathname : '/dashboard');

  const { data: unreadData } = useQuery({
    queryKey: ['unreadCount'],
    queryFn: getUnreadMessageCount,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const count = unreadData?.count || 0;

  const menuItems = [
    { 
      icon: AiFillHome, 
      label: 'Dashboard', 
      path: '/dashboard'
    },
    { 
      icon: BsGrid1X2, 
      label: 'Gigs', 
      path: '/gigs'
    },
    // { 
    //   icon: Grid2X2PlusIcon, 
    //   label: 'Add New Gig', 
    //   path: '/new-gig'
    // },
    { 
      icon: BiCategoryAlt, 
      label: 'Add New Category', 
      path: '/new-category'
    },
    { 
      icon: Users2Icon, 
      label: 'Hellocians', 
      path: '/hellocians'
    },
    // Only show Admins menu for super-admin
    ...(role === 'super-admin' ? [{
      icon: Users2Icon,
      label: 'Admins',
      path: '/manage'
    }] : []),
    { 
      icon: BsGridFill, 
      label: 'Projects', 
      path: '/projects'
    },
    { 
      icon: BiEnvelope, 
      label: 'Project Requests', 
      path: '/project-requests'
    },
    { 
      icon: BiEnvelope, 
      label: (
        <>
          Messages <span className="text-green-500">({count})</span>
        </>
      ),
      path: '/messages'
    },
    { 
      icon: User, 
      label: 'Clients Data', 
      path: '/clients'
    },
    { 
      icon: Settings, 
      label: 'Settings', 
      path: '/settings'
    }
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const logoutUser = async () => {
    try {
      await AxiosInstance.post("/auth/logout");
  
      clearTokens();
      navigate("/");
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      {/* Mobile Header */}
      <MobileHeader onMenuToggle={toggleMobileMenu} />
      
      {/* Mobile Sidebar Overlay */}
      <MobileSidebarOverlay 
        isOpen={isMobileMenuOpen}
        onClose={closeMobileMenu}
        currentPath={activePath}
        onNavigate={onNavigate}
        menuItems={menuItems}
        logoutUser={logoutUser}
      />
      
      {/* Desktop Sidebar */}
      <DesktopSidebar 
        currentPath={activePath}
        onNavigate={onNavigate}
        isCollapsed={collapsed}
        onToggle={toggleSidebar}
        menuItems={menuItems}
        logoutUser={logoutUser}
      />
    </>
  );
};

// Layout Component - This is what you'll use in your pages
export const AdminSidebarLayout = ({ children, currentPath, onNavigate }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0A0A0A]">
      <AdminSidebar 
        currentPath={currentPath} 
        onNavigate={onNavigate}
        isCollapsed={isCollapsed}
        onToggle={toggleSidebar}
      />
      
      {/* Content Area */}
      <div className={`transition-all duration-300 pt-14 md:pt-0 ${
        isCollapsed ? 'md:ml-16' : 'md:ml-64'
      }`}>
        {children}
      </div>
    </div>
  );
};

export default AdminSidebar;